import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBars,
  faUser,
  faSignOutAlt,
  faMap,
  faChartLine,
  faEnvelope,
  faBug, // eslint-disable-line @typescript-eslint/no-unused-vars
  faUserShield,
  faGaugeHigh,
  faHeartPulse,
  faBell, faFileLines } from '@fortawesome/free-solid-svg-icons'
import { useAuthContext, useAccountContext } from '../contexts'
import { Logo } from './Logo'
import { FEATURE_FLAGS } from '../utils/featureFlags' // eslint-disable-line @typescript-eslint/no-unused-vars
import { useAdminAuth } from '../hooks/useAdminAuth'
import { usePartnerAuth } from '../hooks/usePartnerAuth'
import { HealthDot } from './admin/systemHealth/HealthDot'
import { faHandshake } from '@fortawesome/free-solid-svg-icons'
import { useReportsAccess } from '../hooks/useReports'

interface NewNavBarProps {
  onToggleSidebar?: () => void
}

export function NewNavBar({ onToggleSidebar }: NewNavBarProps) {
  const { isAuthorized, signOut } = useAuthContext()
  const { selectDevice } = useAccountContext()
  const { isAdmin } = useAdminAuth()
  const { isPartner } = usePartnerAuth()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Preserve sigfoxId query parameter across navigation
  const sigfoxId = searchParams.get('sigfoxId')
  const queryString = useMemo(() => {
    return sigfoxId ? `?sigfoxId=${sigfoxId}` : ''
  }, [sigfoxId])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    setShowUserMenu(false)
    await signOut()
  }

  // Determine current view for highlighting
  const isDashboardView = location.pathname === '/dashboard'
  const isMapView = location.pathname === '/' || location.pathname === '/map'
  const isChartView = location.pathname === '/charts'
  const isMessagesView = location.pathname.startsWith('/messages')
  const isReportsView = location.pathname.startsWith('/reports')
  // Reports is not a universal section: it appears only for administrators
  // and for users belonging to a group that has a report configured.
  const { data: hasReportsAccess } = useReportsAccess()
  const isAdminView = location.pathname.startsWith('/admin')
  const isSystemHealthView = location.pathname.startsWith('/system-health')
  const isAlertsView = location.pathname.startsWith('/admin-alerts')
  const isPartnerView = location.pathname.startsWith('/partner')

  if (!isAuthorized) {
    return (
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Logo shrink={true} />
            </div>
          </div>
        </div>
      </nav>
    )
  }

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden touch-manipulation"
                aria-label="Toggle sidebar"
              >
                <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
              </button>
            )}

            <Logo shrink={true} />
          </div>

          <div className="flex items-center space-x-3">
            {/* Partner Portal - Only show for partner users */}
            {isPartner && (
              <Link
                to="/partner"
                className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                  isPartnerView
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
                aria-label="Partner Portal"
              >
                <FontAwesomeIcon icon={faHandshake} className="h-5 w-5 mb-1" fixedWidth />
                <span className="text-xs font-medium">Partner</span>
              </Link>
            )}

            {/* System Health - admin only */}
            {isAdmin && (
              <Link
                to="/system-health"
                className={`relative flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                  isSystemHealthView
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
                aria-label="System Health"
              >
                <FontAwesomeIcon icon={faHeartPulse} className="h-5 w-5 mb-1" fixedWidth />
                <span className="text-xs font-medium">Health</span>
                <HealthDot className="absolute top-1 right-1" />
              </Link>
            )}

            {/* Alerts - admin only */}
            {isAdmin && (
              <Link
                to="/admin-alerts"
                className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                  isAlertsView
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
                aria-label="Alerts"
              >
                <FontAwesomeIcon icon={faBell} className="h-5 w-5 mb-1" fixedWidth />
                <span className="text-xs font-medium">Alerts</span>
              </Link>
            )}

            {/* Admin Panel - Only show for admin users */}
            {FEATURE_FLAGS.ADMIN_SECTION && isAdmin && (
              <Link
                to="/admin"
                className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                  isAdminView
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
                aria-label="Admin Panel"
              >
                <FontAwesomeIcon icon={faUserShield} className="h-5 w-5 mb-1" fixedWidth />
                <span className="text-xs font-medium">Admin</span>
              </Link>
            )}

            {/* Dashboard */}
            {/* On the dashboard itself this is the way back from a device to the
                fleet overview, so it drops the selection instead of keeping it. */}
            <Link
              to={isDashboardView ? '/dashboard' : `/dashboard${queryString}`}
              onClick={() => {
                if (isDashboardView) selectDevice('')
              }}
              className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                isDashboardView
                  ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Dashboard"
            >
              <FontAwesomeIcon icon={faGaugeHigh} className="h-5 w-5 mb-1" fixedWidth />
              <span className="text-xs font-medium">Dashboard</span>
            </Link>

            {/* Map View */}
            <Link
              to={`/map${queryString}`}
              className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                isMapView
                  ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Map View"
            >
              <FontAwesomeIcon icon={faMap} className="h-5 w-5 mb-1" fixedWidth />
              <span className="text-xs font-medium">Map</span>
            </Link>

            {/* Charts View */}
            <Link
              to={`/charts${queryString}`}
              className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                isChartView
                  ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Charts View"
            >
              <FontAwesomeIcon icon={faChartLine} className="h-5 w-5 mb-1" fixedWidth />
              <span className="text-xs font-medium">Charts</span>
            </Link>

            {/* Alerts */}
            {/* <Link
              to={`/alerts${queryString}`}
              className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                isAlertsView
                  ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Alerts"
            >
              <FontAwesomeIcon icon={faBell} className="h-5 w-5 mb-1" fixedWidth />
              <span className="text-xs font-medium">Alerts</span>
            </Link> */}

            {/* Sensor Management */}
            {/* <Link
              to={`/sensors${queryString}`}
              className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                isSensorsView
                  ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Sensor Management"
            >
              <FontAwesomeIcon icon={faWrench} className="h-5 w-5 mb-1" fixedWidth />
              <span className="text-xs font-medium">Sensors</span>
            </Link> */}

            {/* Reports. Shown only to administrators and to users whose group
                has a report configured. No query string: a report is a stored
                record and does not belong to the current device or time
                selection the way Map, Charts and Messages do. */}
            {hasReportsAccess && (
              <Link
                to="/reports"
                className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                  isReportsView
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
                aria-label="Reports"
              >
                <FontAwesomeIcon icon={faFileLines} className="h-5 w-5 mb-1" fixedWidth />
                <span className="text-xs font-medium">Reports</span>
              </Link>
            )}

            {/* Messages */}
            <Link
              to={`/messages${queryString}`}
              className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                isMessagesView
                  ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Messages"
            >
              <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5 mb-1" fixedWidth />
              <span className="text-xs font-medium">Messages</span>
            </Link>

            {/* Debug - Only show when debug mode is enabled */}
            {FEATURE_FLAGS.DEBUG_MODE && (
              <Link
                to="/messages"
                className={`flex flex-col items-center px-2 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px] ${
                  isMessagesView
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
                aria-label="Debug Messages"
              >
                <FontAwesomeIcon icon={faBug} className="h-5 w-5 mb-1" fixedWidth />
                <span className="text-xs font-medium">Debug</span>
              </Link>
            )}

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex flex-col items-center px-2 py-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation transition-all duration-200 min-w-[60px]"
                aria-label="User menu"
                aria-expanded={showUserMenu}
              >
                <FontAwesomeIcon icon={faUser} className="h-5 w-5 mb-1" fixedWidth />
                <span className="text-xs font-medium">Account</span>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <Link
                    to="/profile"
                    className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                    onClick={() => setShowUserMenu(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                    onClick={() => setShowUserMenu(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}
